"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Field,
  Notice,
  SelectField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  HINT,
  INPUT_MONO,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import {
  type Depth,
  drawQuestions,
  type Icebreaker,
  planSession,
  questionPool,
  type Setting,
  swapQuestion,
} from "@/lib/tool-kit/random-fun/icebreakers";

/** English strings. A translated content file overrides these through `ui`. */
export const ICEBREAKER_QUESTION_GENERATOR_STRINGS = {
  settingLabel: "Setting",
  settingClassroom: "Classroom",
  settingWorkplace: "Team meeting or workplace",
  settingAnywhere: "Anywhere (works in both)",
  depthLabel: "Depth",
  depthAny: "Any depth",
  depth1: "Light — a few words",
  depth2: "Medium — a short story",
  depth3: "Deeper — a reflection",
  depthTag1: "Light",
  depthTag2: "Medium",
  depthTag3: "Deeper",
  peopleLabel: "People in the group",
  countLabel: "Questions to draw",
  generate: "Get icebreaker questions",
  results: "Your questions",
  placeholder: "Press the button to draw questions.",
  swap: "Swap",
  swapLabel: "Swap question {number}",
  noSwap: "No other questions match these settings.",
  copyAll: "Copy all questions",
  copied: "Copied!",
  announce: "Drew {count}: {first}",
  problemPeople: "Enter how many people are in the group, from 1 to 500.",
  problemCount: "Enter how many questions to draw, from 1 to 10.",
  timingHeading: "How long one round takes",
  timingHint:
    "Planning estimates: about 20 seconds per person for a light question, 45 for a medium one and 90 for a deeper one, with a ten-minute limit for a round.",
  timingCaption: "One question, everyone answers once, for {n} {peopleWord}",
  peopleWord: "one: person|other: people",
  colDepth: "Depth",
  colWholeGroup: "Whole group",
  colAdvice: "Advice",
  minutes: "one: {count} minute|other: {count} minutes",
  adviceWhole: "Fits in one round.",
  adviceSplit:
    "Split into {groups} groups of about {size}; each round then takes about {time}.",
  poolNote: "{count} {questionsWord} match these settings.",
  questionsWord: "one: question|other: questions",
} as const;

type Strings = typeof ICEBREAKER_QUESTION_GENERATOR_STRINGS;

const DEPTHS: readonly Depth[] = [1, 2, 3];
const MAX_PEOPLE = 500;
const MAX_QUESTIONS = 10;
const DEPTH_TAG = {
  1: "depthTag1",
  2: "depthTag2",
  3: "depthTag3",
} as const;

export default function IcebreakerQuestionGeneratorWidget() {
  const strings: Strings = useToolUi(ICEBREAKER_QUESTION_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [setting, setSetting] = useState<Setting>("classroom");
  const [depth, setDepth] = useState("any");
  const [peopleText, setPeopleText] = useState("12");
  const [count, setCount] = useState("3");
  const [shown, setShown] = useState<Icebreaker[]>([]);
  const [error, setError] = useState("");
  const [swapNote, setSwapNote] = useState("");
  const [announcement, setAnnouncement] = useState("");

  const depthFilter: Depth | "any" =
    depth === "any" ? "any" : (Number(depth) as Depth);
  const pool = questionPool(setting, depthFilter);
  const people = parseIntegerInput(peopleText);
  const validPeople = people !== null && people >= 1 && people <= MAX_PEOPLE;

  function minutesText(value: number): string {
    return tPlural(strings.minutes, locale, value, { count: fmt(value) });
  }

  function generate() {
    if (!validPeople) {
      setError(strings.problemPeople);
      return;
    }
    const wanted = Number(count);
    if (!(wanted >= 1 && wanted <= MAX_QUESTIONS)) {
      setError(strings.problemCount);
      return;
    }
    const drawn = drawQuestions(pool, wanted);
    setError("");
    setSwapNote("");
    setShown(drawn);
    setAnnouncement(
      t(strings.announce, {
        count: fmt(drawn.length),
        first: drawn[0]?.text ?? "",
      })
    );
  }

  function swap(index: number) {
    const replacement = swapQuestion(pool, shown);
    if (!replacement) {
      setSwapNote(strings.noSwap);
      return;
    }
    setSwapNote("");
    setShown(shown.map((q, i) => (i === index ? replacement : q)));
    setAnnouncement(replacement.text);
  }

  const timingRows = validPeople
    ? DEPTHS.map((d) => {
        const plan = planSession(people, d);
        return [
          strings[DEPTH_TAG[d]],
          minutesText(plan.minutes),
          plan.split
            ? t(strings.adviceSplit, {
                groups: fmt(plan.groups),
                size: fmt(plan.groupSize),
                time: minutesText(plan.groupMinutes),
              })
            : strings.adviceWhole,
        ];
      })
    : [];

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField
          id="iqg-setting"
          label={strings.settingLabel}
          onChange={setSetting}
          options={[
            { value: "classroom", label: strings.settingClassroom },
            { value: "workplace", label: strings.settingWorkplace },
            { value: "anywhere", label: strings.settingAnywhere },
          ]}
          value={setting}
        />
        <SelectField
          id="iqg-depth"
          label={strings.depthLabel}
          onChange={setDepth}
          options={[
            { value: "any", label: strings.depthAny },
            { value: "1", label: strings.depth1 },
            { value: "2", label: strings.depth2 },
            { value: "3", label: strings.depth3 },
          ]}
          value={depth}
        />
        <Field id="iqg-people" label={strings.peopleLabel}>
          <input
            className={INPUT_MONO}
            id="iqg-people"
            inputMode="numeric"
            onChange={(event) => setPeopleText(event.target.value)}
            type="text"
            value={peopleText}
          />
        </Field>
        <SelectField
          id="iqg-count"
          label={strings.countLabel}
          onChange={setCount}
          options={Array.from({ length: MAX_QUESTIONS }, (_, i) => ({
            value: String(i + 1),
            label: fmt(i + 1),
          }))}
          value={count}
        />
      </div>
      <p className={HINT}>
        {t(strings.poolNote, {
          count: fmt(pool.length),
          questionsWord: tPlural(strings.questionsWord, locale, pool.length),
        })}
      </p>

      <button className={BUTTON_PRIMARY} onClick={generate} type="button">
        {strings.generate}
      </button>
      {error ? <ErrorNotice>{error}</ErrorNotice> : null}

      <section className="space-y-3">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <p aria-live="polite" className="sr-only">
          {announcement}
        </p>
        {shown.length === 0 ? (
          <Notice>{strings.placeholder}</Notice>
        ) : (
          <>
            <ol className="space-y-2">
              {shown.map((question, index) => (
                <li
                  className="flex items-start justify-between gap-3 rounded-lg border border-border bg-card p-4"
                  key={question.text}
                >
                  <div className="space-y-1">
                    <p className="text-foreground">{question.text}</p>
                    <p className={HINT}>{strings[DEPTH_TAG[question.depth]]}</p>
                  </div>
                  <button
                    aria-label={t(strings.swapLabel, {
                      number: fmt(index + 1),
                    })}
                    className={BUTTON_SMALL}
                    onClick={() => swap(index)}
                    type="button"
                  >
                    {strings.swap}
                  </button>
                </li>
              ))}
            </ol>
            {swapNote ? <Notice>{swapNote}</Notice> : null}
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyAll}
              text={shown.map((q, i) => `${i + 1}. ${q.text}`).join("\n")}
            />
          </>
        )}
      </section>

      {validPeople ? (
        <section className="space-y-3">
          <h2 className={WIDGET_H3}>{strings.timingHeading}</h2>
          <DataTable
            caption={t(strings.timingCaption, {
              n: fmt(people),
              peopleWord: tPlural(strings.peopleWord, locale, people),
            })}
            head={[strings.colDepth, strings.colWholeGroup, strings.colAdvice]}
            rows={timingRows}
          />
          <p className={HINT}>{strings.timingHint}</p>
        </section>
      ) : null}
    </div>
  );
}
